// Centralized date formatting helpers.
// All UI-displayed dates use European order: day-month-year.
// Numeric dates are DD-MM-YY; textual parts (month names, weekdays) use en-GB.

type DateInput = Date | string | number | null | undefined;

const toDate = (input: DateInput): Date | null => {
  if (input == null || input === '') return null;
  const d = input instanceof Date ? input : new Date(input);
  return isNaN(d.getTime()) ? null : d;
};

const pad = (n: number) => String(n).padStart(2, '0');

// Fixed short month names: en-GB in recent browsers prints "Sept", which sits
// badly next to "Aug" and "Oct" on chart axes.
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const mon = (d: Date) => MONTHS_SHORT[d.getMonth()];

/**
 * Convert an ISO YYYY-MM-DD string to DD-MM-YY via pure string ops.
 * Use this when the input is already a calendar date (no time component) to
 * avoid timezone shifts that `new Date('YYYY-MM-DD')` introduces.
 */
export const formatIsoDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return '';
  return `${m[3]}-${m[2]}-${m[1]!.slice(-2)}`;
};

/** DD/MM/YYYY via pure string ops — same timezone-safe contract as formatIsoDate. */
export const formatIsoDateSlash = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return '';
  return `${m[3]}/${m[2]}/${m[1]}`;
};

/** DD/MM/YYYY (e.g. 20/05/2026) */
export const formatDateSlash = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

/** DD-MM-YY (e.g. 20-05-26) */
export const formatDate = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${String(d.getFullYear()).slice(-2)}`;
};

/** DD-MM-YYYY (e.g. 20-05-2026) — for places where the full year matters */
export const formatDateLong = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
};

/** HH:MM (24h) */
export const formatTime = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** DD-MM-YY HH:MM */
export const formatDateTime = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return `${formatDate(d)} ${formatTime(d)}`;
};

/** DD-MM-YYYY HH:MM */
export const formatDateTimeLong = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return `${formatDateLong(d)} ${formatTime(d)}`;
};

/** "May 2026" */
export const formatMonthYear = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

/** "May 26" */
export const formatMonthYearShort = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return `${mon(d)} ${String(d.getFullYear()).slice(-2)}`;
};

/** "20 May" */
export const formatDayMonth = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return `${d.getDate()} ${mon(d)}`;
};

/** "20 May 2026" */
export const formatDayMonthYear = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return `${d.getDate()} ${mon(d)} ${d.getFullYear()}`;
};

/** "20 May 2026, 14:30" */
export const formatDayMonthYearTime = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return `${formatDayMonthYear(d)}, ${formatTime(d)}`;
};

/** "Mon" */
export const formatWeekday = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleDateString('en-GB', { weekday: 'short' });
};

/** "Monday" */
export const formatWeekdayLong = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleDateString('en-GB', { weekday: 'long' });
};

/** "Mon 20 May" */
export const formatWeekdayDayMonth = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${d.getDate()} ${mon(d)}`;
};

/** "20 May, 14:30" — short chat-style timestamp */
export const formatDayMonthTime = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return `${formatDayMonth(d)}, ${formatTime(d)}`;
};

/** "Monday, 20 May 2026" — long, readable */
export const formatLongDate = (input: DateInput): string => {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};
